import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, History } from 'lucide-react';
import apiService from '../../services/apiService';

/* ── 애니메이션 ── */
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(-8px); background: rgba(0,198,255,0.08); }
  to   { opacity: 1; transform: translateY(0);    background: transparent; }
`;
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;
const scanFlash = keyframes`
  0%   { border-color: #0072ff; box-shadow: 0 0 0 0 rgba(0,114,255,0.4); }
  70%  { border-color: #00c6ff; box-shadow: 0 0 0 8px rgba(0,114,255,0); }
  100% { border-color: #0072ff; box-shadow: 0 0 0 0 rgba(0,114,255,0); }
`;

/* ── 레이아웃 ── */
const Page = styled.div`
  min-height: 100vh;
  background: #0a0a0f;
  color: #e2e8f0;
  font-family: 'Inter', 'Pretendard', sans-serif;
  padding: 20px;
`;
const TopBar = styled.div`
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 24px; padding-bottom: 20px; border-bottom: 1px solid #1e2030;
  flex-wrap: wrap;
`;
const BackBtn = styled.button`
  background: none; border: none; color: #64748b; cursor: pointer;
  padding: 8px; border-radius: 8px;
  &:hover { background: #1e2030; color: #e2e8f0; }
`;
const TitleArea = styled.div`flex: 1; min-width: 180px;`;
const PageTitle = styled.h1`
  font-size: 22px; font-weight: 800; margin: 0;
  background: linear-gradient(135deg, #00c6ff, #0072ff);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
`;
const PageSub = styled.p`margin: 2px 0 0; font-size: 12px; color: #64748b;`;

const Toolbar = styled.div`
  display: flex; gap: 10px; align-items: center; margin-bottom: 20px; flex-wrap: wrap;
`;
const Select = styled.select`
  padding: 8px 12px; background: #111827; border: 1px solid #374151;
  border-radius: 8px; color: #e2e8f0; font-size: 13px;
  &:focus { outline: none; border-color: #0072ff; }
`;
const NumInput = styled.input`
  padding: 8px 10px; background: #111827; border: 1px solid #374151;
  border-radius: 8px; color: #e2e8f0; font-size: 13px; width: 88px;
  text-align: right;
  &:focus { outline: none; border-color: #0072ff; }
  &:disabled { opacity: 0.4; }
`;
const Btn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 10px 18px; border-radius: 10px; font-size: 13px; font-weight: 600;
  cursor: pointer; border: none;
  background: ${p => p.primary ? 'rgba(0,114,255,0.15)' : '#1e2030'};
  color: ${p => p.primary ? '#00c6ff' : '#94a3b8'};
  border: 1px solid ${p => p.primary ? '#0072ff' : '#374151'};
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

/* ── 스캔 현황 패널 ── */
const ScanPanel = styled.div`
  background: #0d1117;
  border: 1px solid ${p => p.active ? '#0072ff' : '#1e2030'};
  border-radius: 14px;
  padding: 16px 20px;
  margin-bottom: 16px;
  animation: ${p => p.active ? scanFlash : 'none'} 2s ease infinite;
`;
const ScanHeader = styled.div`
  display: flex; align-items: center; gap: 10px; margin-bottom: 12px;
`;
const ScanDot = styled.div`
  width: 8px; height: 8px; border-radius: 50%;
  background: ${p => p.active ? '#00c6ff' : '#374151'};
  animation: ${p => p.active ? pulse : 'none'} 1.2s ease infinite;
`;
const ScanLabel = styled.span`font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em;`;
const ScanStock = styled.div`
  font-size: 16px; font-weight: 700; color: #e2e8f0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const ScanCode = styled.span`font-size: 11px; color: #475569; margin-left: 8px; font-weight: 400;`;

const ProgressTrack = styled.div`height: 5px; background: #1e2030; border-radius: 3px; overflow: hidden; margin-top: 10px;`;
const ProgressFill = styled.div`
  height: 100%; border-radius: 3px; transition: width 0.4s ease;
  background: linear-gradient(90deg, #0072ff, #00c6ff);
`;
const ProgressMeta = styled.div`
  display: flex; justify-content: space-between; margin-top: 6px;
  font-size: 11px; color: #475569;
`;

/* ── 통계 카드 ── */
const StatsRow = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 16px;
  @media (max-width: 560px) { grid-template-columns: repeat(2, 1fr); }
`;
const StatCard = styled.div`
  background: #111827; border: 1px solid #1e2030; border-radius: 10px;
  padding: 12px 14px; text-align: center;
`;
const StatNum = styled.div`font-size: 22px; font-weight: 800; color: ${p => p.color || '#e2e8f0'};`;
const StatLabel = styled.div`font-size: 10px; color: #64748b; margin-top: 2px;`;

/* ── 결과 테이블 ── */
const ResultsSection = styled.div`
  background: #111827;
  border: 1px solid #1e2030;
  border-radius: 14px;
  overflow: hidden;
`;
const ResultsHeader = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 14px 16px; border-bottom: 1px solid #1e2030;
  background: #0d1117;
`;
const ResultsTitle = styled.span`font-size: 13px; font-weight: 700; color: #e2e8f0;`;
const HitsBadge = styled.span`
  font-size: 11px; font-weight: 700; padding: 2px 9px; border-radius: 99px;
  background: rgba(0,198,255,0.12); color: #00c6ff;
`;

const COLS = '44px 1fr 80px 90px 80px 76px 74px 80px';
const TH = styled.div`
  display: grid; grid-template-columns: ${COLS}; gap: 8px;
  padding: 10px 16px;
  font-size: 10px; color: #64748b; font-weight: 700; text-transform: uppercase;
  border-bottom: 1px solid #1e2030;
  min-width: 580px;
`;
const TR = styled.div`
  display: grid; grid-template-columns: ${COLS}; gap: 8px;
  padding: 11px 16px; border-bottom: 1px solid #1e2030;
  font-size: 12px; min-width: 580px; transition: background 0.15s;
  animation: ${fadeSlideIn} 0.4s ease;
  &:hover { background: #1a1f2e; }
  &:last-child { border-bottom: none; }
`;
const Badge = styled.span`
  font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 5px;
  background: rgba(16,185,129,0.12); color: #10b981;
`;
const SlopeTag = styled.span`
  font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 5px;
  background: rgba(245,158,11,0.12); color: #f59e0b;
`;

const EmptyHits = styled.div`
  padding: 32px; text-align: center; color: #374151; font-size: 13px;
`;

const OverflowX = styled.div`overflow-x: auto;`;

const EmptyState = styled.div`
  text-align: center; padding: 60px 20px; color: #475569;
  background: #111827; border-radius: 14px;
`;
const KisWarningBanner = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 14px; margin-bottom: 14px;
  background: rgba(245,158,11,0.08); border: 1px solid rgba(245,158,11,0.3);
  border-radius: 10px; font-size: 12px; color: #f59e0b; line-height: 1.5;
`;

function VwapScreenerPage() {
  const navigate = useNavigate();
  const [market, setMarket] = useState('ALL');
  const [minMarketCap, setMinMarketCap] = useState(2000);
  const [running, setRunning] = useState(false);
  const [current, setCurrent] = useState({ symbol: '', name: '', scanned: 0, total: 0 });
  const [hits, setHits] = useState([]);
  const [skipped, setSkipped] = useState(0);
  const [done, setDone] = useState(false);
  const [kisStats, setKisStats] = useState(null);
  const [history, setHistory] = useState([]);
  const tableEndRef = useRef(null);

  useEffect(() => {
    apiService.getVwapScreenerHistory()
      .then(data => setHistory(data || []))
      .catch(() => {});
  }, []);

  /* 새 hit 추가될 때마다 테이블 하단 스크롤 */
  useEffect(() => {
    if (hits.length > 0) {
      tableEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [hits.length]);

  const handleRun = async () => {
    setRunning(true);
    setDone(false);
    setHits([]);
    setSkipped(0);
    setKisStats(null);
    setCurrent({ symbol: '', name: '', scanned: 0, total: 0 });

    try {
      await apiService.runVwapScreener({ market, minMarketCap }, (data) => {
        if (data.type === 'kisWarning') {
          setKisStats({ headerFailed: true, message: data.message });
        } else if (data.type === 'start') {
          setCurrent(prev => ({ ...prev, total: data.total }));
        } else if (data.type === 'progress') {
          setCurrent({ symbol: data.symbol, name: data.name, scanned: data.current, total: data.total });
        } else if (data.type === 'hit') {
          setHits(prev => [...prev, data]);
        } else if (data.type === 'skip') {
          setSkipped(prev => prev + 1);
        } else if (data.type === 'done') {
          if (data.kisStats) setKisStats(data.kisStats);
          setDone(true);
          apiService.getVwapScreenerHistory().then(d => setHistory(d || [])).catch(() => {});
        }
      });
    } catch (e) {
      alert('스크리닝 오류: ' + e.message);
    } finally {
      setRunning(false);
    }
  };

  const pct = current.total > 0 ? (current.scanned / current.total) * 100 : 0;
  const fmt = n => n != null ? Number(n).toLocaleString() : '-';
  const fmtMcap = v => {
    if (!v) return '-';
    if (v >= 10000) return (v / 10000).toFixed(1) + '조';
    return v.toLocaleString() + '억';
  };
  const showPanel = running || done || current.total > 0;

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs')}><ArrowLeft size={18} /></BackBtn>
        <TitleArea>
          <PageTitle>VWAP 급등 스크리너</PageTitle>
          <PageSub>정배열 · 기울기 · 이격도 · 변동성 수축 — 전 종목 스캔</PageSub>
        </TitleArea>
        <Btn onClick={() => navigate('/labs/vwap-screener/history')}>
          <History size={14} /> 이력
        </Btn>
      </TopBar>

      <Toolbar>
        <Select value={market} onChange={e => setMarket(e.target.value)} disabled={running}>
          <option value="ALL">전체 시장</option>
          <option value="KOSPI">KOSPI</option>
          <option value="KOSDAQ">KOSDAQ</option>
        </Select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#94a3b8' }}>
          시가총액 ≥
          <NumInput
            type="number"
            value={minMarketCap}
            onChange={e => setMinMarketCap(Number(e.target.value))}
            min={0}
            step={100}
            disabled={running}
          />
          억원
        </label>
        <Btn primary onClick={handleRun} disabled={running}>
          <RefreshCw size={14} style={{ animation: running ? 'spin 1s linear infinite' : 'none' }} />
          {running ? '스캔 중...' : '스캔 실행'}
        </Btn>
        {done && <span style={{ fontSize: 12, color: '#10b981' }}>✓ 스캔 완료</span>}
      </Toolbar>

      {/* ── KIS API 경고 배너 ── */}
      {kisStats && (kisStats.headerFailed || kisStats.failures > 0) && (
        <KisWarningBanner>
          <span>⚠</span>
          <div>
            {kisStats.headerFailed
              ? <><strong>KIS API 인증 실패</strong> — 시가총액을 실시간으로 가져오지 못했습니다. 정적 데이터(stock_codes.json)로 대체되었으므로 시가총액 필터 정확도가 낮을 수 있습니다.<br/>{kisStats.message && <span style={{ color: '#94a3b8', fontSize: 11 }}>{kisStats.message}</span>}</>
              : <><strong>KIS 시가총액 조회 일부 실패</strong> — {kisStats.attempts}건 중 {kisStats.failures}건({((kisStats.failures / kisStats.attempts) * 100).toFixed(1)}%) 조회 실패. 해당 종목은 정적 데이터로 대체되었습니다.</>
            }
          </div>
        </KisWarningBanner>
      )}

      {/* ── 실시간 스캔 현황 패널 ── */}
      {showPanel && (
        <>
          <ScanPanel active={running}>
            <ScanHeader>
              <ScanDot active={running} />
              <ScanLabel>{running ? '실시간 스캔 중' : '스캔 완료'}</ScanLabel>
            </ScanHeader>
            <ScanStock>
              {running
                ? (current.name
                    ? <>{current.name}<ScanCode>{current.symbol}</ScanCode></>
                    : <span style={{ color: '#475569' }}>초기화 중...</span>)
                : <span style={{ color: '#10b981' }}>전체 {fmt(current.total)}종목 완료</span>
              }
            </ScanStock>
            <ProgressTrack>
              <ProgressFill style={{ width: `${pct}%` }} />
            </ProgressTrack>
            <ProgressMeta>
              <span>{fmt(current.scanned)} / {fmt(current.total)} 종목</span>
              <span>{pct.toFixed(1)}%</span>
            </ProgressMeta>
          </ScanPanel>

          <StatsRow>
            <StatCard>
              <StatNum color="#94a3b8">{fmt(current.total)}</StatNum>
              <StatLabel>전체 종목</StatLabel>
            </StatCard>
            <StatCard>
              <StatNum color="#60a5fa">{fmt(current.scanned)}</StatNum>
              <StatLabel>스캔 완료</StatLabel>
            </StatCard>
            <StatCard>
              <StatNum color="#00c6ff">{hits.length}</StatNum>
              <StatLabel>발굴 종목</StatLabel>
            </StatCard>
            <StatCard>
              <StatNum color="#374151">{fmt(skipped)}</StatNum>
              <StatLabel>조건 미달</StatLabel>
            </StatCard>
          </StatsRow>

          {/* ── 결과 테이블 (스캔 시작부터 항상 표시) ── */}
          <ResultsSection>
            <ResultsHeader>
              <ResultsTitle>발굴 종목</ResultsTitle>
              {hits.length > 0 && <HitsBadge>{hits.length}개</HitsBadge>}
              {running && hits.length === 0 && (
                <span style={{ fontSize: 11, color: '#475569', marginLeft: 4 }}>조건 충족 종목 발견 시 즉시 표시됩니다</span>
              )}
            </ResultsHeader>

            {hits.length > 0 ? (
              <OverflowX>
                <TH>
                  <span>#</span>
                  <span>종목</span>
                  <span>시가총액</span>
                  <span>현재가</span>
                  <span>VWAP20</span>
                  <span>이격도</span>
                  <span>기울기</span>
                  <span>ATR(14)</span>
                </TH>
                {hits.map((h, i) => (
                  <TR key={h.symbol}>
                    <span style={{ color: '#475569', fontSize: 11 }}>{i + 1}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{h.name}</div>
                      <div style={{ fontSize: 10, color: '#475569', marginTop: 1 }}>{h.symbol} · {h.market}</div>
                    </div>
                    <span style={{ color: '#94a3b8', fontSize: 11 }}>{fmtMcap(h.marketCap)}</span>
                    <span style={{ fontWeight: 700 }}>{fmt(h.close)}</span>
                    <span style={{ color: '#94a3b8' }}>{fmt(h.vwap20)}</span>
                    <Badge>{h.disparity}%</Badge>
                    <SlopeTag>+{h.slope}%</SlopeTag>
                    <span style={{ color: '#64748b', fontSize: 11 }}>{fmt(h.atr14)}</span>
                  </TR>
                ))}
                <div ref={tableEndRef} />
              </OverflowX>
            ) : (
              <EmptyHits>
                {running ? '⏳ 조건 충족 종목을 탐색하고 있습니다...' : '조건을 충족하는 종목이 없습니다.'}
              </EmptyHits>
            )}
          </ResultsSection>
        </>
      )}

      {/* ── 초기 상태 ── */}
      {!showPanel && (
        <EmptyState>
          <div style={{ fontSize: 36, marginBottom: 14 }}>📡</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
            스캔 실행 버튼을 눌러 VWAP 조건 종목을 탐색하세요
          </div>
          <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.7 }}>
            ① VWAP20 &gt; VWAP72 &nbsp;② 기울기 0~2% &nbsp;③ 이격도 100~103% &nbsp;④ ATR 수축
          </div>
          {history.length > 0 && (
            <div style={{ marginTop: 28 }}>
              <div style={{ fontSize: 12, color: '#475569', marginBottom: 10 }}>최근 스캔 이력</div>
              {history.slice(0, 3).map(h => (
                <div key={h.id}
                  onClick={() => navigate('/labs/vwap-screener/history')}
                  style={{ display: 'inline-flex', gap: 12, padding: '6px 14px', margin: '4px',
                    background: '#111827', border: '1px solid #1e2030', borderRadius: 8,
                    fontSize: 12, color: '#94a3b8', cursor: 'pointer' }}>
                  <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{h.date}</span>
                  <span style={{ color: '#00c6ff' }}>발굴 {h.hitsCount}종목</span>
                </div>
              ))}
            </div>
          )}
        </EmptyState>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </Page>
  );
}

export default VwapScreenerPage;
